import React, { useState, useEffect, useCallback } from "react";
import { Plus, PlayCircle, RotateCw, X, Eye, Rocket, CircleDot, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import api, { notifyError } from "@/lib/api";
import PlatformLayout from "@/components/PlatformLayout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import DeploymentSubNav from "./deployments/DeploymentSubNav";
import { RunStatusBadge } from "./deployments/RunBadges";
import DeploymentRunFormDialog from "./deployments/DeploymentRunFormDialog";

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}
function dur(sec) {
  if (sec == null) return "—";
  const s = Math.round(sec);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${s % 60}s`;
}

function SummaryCard({ label, value, tone = "neutral", icon: Icon, testid }) {
  const tones = {
    neutral: "text-white/70",
    running: "text-amber-300",
    success: "text-emerald-300",
    failed: "text-rose-300",
    pending: "text-sky-300",
  };
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1.5">
        {Icon ? <Icon size={14} className={tones[tone]} /> : null}
        <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold">{label}</div>
      </div>
      <div className={`text-2xl font-black font-display ${tones[tone]}`}>{value}</div>
    </div>
  );
}

export default function PlatformDeploymentRuns() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rs, ts] = await Promise.all([
        api.get("/platform/deployment-runner"),
        api.get("/platform/tenants"),
      ]);
      setItems(rs.data.items || []);
      setSummary(rs.data.summary || null);
      setTenants(ts.data.items || []);
    } catch (e) { notifyError(e); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const action = async (id, verb, body = {}) => {
    setBusyId(id + verb);
    try {
      await api.post(`/platform/deployment-runner/${id}/${verb}`, body);
      toast.success(`Run ${verb} berhasil.`);
      load();
    } catch (e) { notifyError(e, `Gagal ${verb}`); }
    finally { setBusyId(null); }
  };

  const cancel = (id) => {
    const reason = window.prompt("Alasan cancel?") || "";
    action(id, "cancel", { reason });
  };

  const tenantOf = (id) => tenants.find((t) => t.tenant_id === id);

  return (
    <PlatformLayout title="Deployment Runs">
      <div data-testid="platform-runs-page">
        <DeploymentSubNav />

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
          <SummaryCard label="Total" value={summary?.total ?? 0} icon={Rocket} testid="run-summary-total" />
          <SummaryCard label="Pending" value={summary?.pending ?? 0} tone="pending" icon={CircleDot} testid="run-summary-pending" />
          <SummaryCard label="Running" value={summary?.running ?? 0} tone="running" icon={Loader2} testid="run-summary-running" />
          <SummaryCard label="Success" value={summary?.success ?? 0} tone="success" icon={CheckCircle2} testid="run-summary-success" />
          <SummaryCard label="Failed" value={summary?.failed ?? 0} tone="failed" icon={XCircle} testid="run-summary-failed" />
        </div>

        <div className="flex items-center gap-3 mb-4">
          <div className="text-xs text-white/50">Menampilkan {items.length} run</div>
          <Button onClick={() => setDialogOpen(true)} className="ml-auto bg-white text-black hover:bg-white/90" data-testid="btn-create-run">
            <Plus size={14} className="mr-1" /> Buat Deployment Run
          </Button>
        </div>

        {loading ? (
          <div className="text-white/40 text-sm p-6 rounded-2xl border border-white/10 text-center">Memuat…</div>
        ) : items.length === 0 ? (
          <div className="text-white/40 text-sm p-8 rounded-2xl border border-white/10 text-center">
            Belum ada deployment run. Klik <strong className="text-white">+ Buat Deployment Run</strong>.
          </div>
        ) : (
          <div className="rounded-2xl border border-white/10 overflow-hidden">
            <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-3 bg-white/5 text-[10px] uppercase tracking-wider text-white/40 font-bold">
              <div className="col-span-3">Run #</div>
              <div className="col-span-3">Tenant</div>
              <div className="col-span-1">Status</div>
              <div className="col-span-1">Artifacts</div>
              <div className="col-span-1">Duration</div>
              <div className="col-span-1">Created</div>
              <div className="col-span-2 text-right">Action</div>
            </div>
            {items.map((r) => {
              const t = tenantOf(r.tenant_id);
              const canCancel = ["PENDING", "RUNNING"].includes(r.status);
              const canExec = r.status === "PENDING";
              const canRetry = r.status === "FAILED";
              const artCount = Object.keys(r.artifact_bundle || {}).length;
              return (
                <div key={r.id} className="grid grid-cols-1 md:grid-cols-12 gap-2 px-4 py-3 border-t border-white/5 items-center"
                  data-testid={`run-row-${r.id}`}>
                  <div className="col-span-3 min-w-0">
                    <Link to={`/platform/deployments/runs/${r.id}`} className="text-sm font-mono font-bold text-white hover:text-emerald-300 truncate block" data-testid={`run-number-${r.id}`}>
                      {r.run_number}
                    </Link>
                    <div className="text-[10px] text-white/40 truncate">{r.triggered_by || "system"}</div>
                  </div>
                  <div className="col-span-3 min-w-0">
                    <div className="text-xs font-bold text-white truncate">{t?.tenant_name || r.tenant_id.slice(0, 8)}</div>
                    <div className="text-[10px] text-white/40 font-mono">{t?.tenant_code || "—"}</div>
                  </div>
                  <div className="col-span-1"><RunStatusBadge status={r.status} size="sm" /></div>
                  <div className="col-span-1 text-xs text-white/60 font-mono">{artCount}</div>
                  <div className="col-span-1 text-xs text-white/60 font-mono">{dur(r.duration_seconds)}</div>
                  <div className="col-span-1 text-[11px] text-white/40">{fmt(r.created_at)}</div>
                  <div className="col-span-2 flex gap-1 justify-end">
                    <Link to={`/platform/deployments/runs/${r.id}`} className="w-7 h-7 rounded-lg grid place-items-center text-white/60 hover:text-white hover:bg-white/10"
                      title="View" data-testid={`run-view-${r.id}`}>
                      <Eye size={12} />
                    </Link>
                    {canExec && (
                      <button onClick={() => action(r.id, "execute", { simulate: "success" })} disabled={busyId === r.id + "execute"}
                        className="w-7 h-7 rounded-lg grid place-items-center text-emerald-400 hover:bg-emerald-500/10 disabled:opacity-50"
                        title="Execute" data-testid={`run-execute-${r.id}`}>
                        <PlayCircle size={12} />
                      </button>
                    )}
                    {canRetry && (
                      <button onClick={() => action(r.id, "retry")} disabled={busyId === r.id + "retry"}
                        className="w-7 h-7 rounded-lg grid place-items-center text-amber-400 hover:bg-amber-500/10 disabled:opacity-50"
                        title="Retry" data-testid={`run-retry-${r.id}`}>
                        <RotateCw size={12} />
                      </button>
                    )}
                    {canCancel && (
                      <button onClick={() => cancel(r.id)} disabled={busyId === r.id + "cancel"}
                        className="w-7 h-7 rounded-lg grid place-items-center text-rose-400 hover:bg-rose-500/10 disabled:opacity-50"
                        title="Cancel" data-testid={`run-cancel-${r.id}`}>
                        <X size={12} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <DeploymentRunFormDialog tenants={tenants} open={dialogOpen} onClose={() => setDialogOpen(false)} onCreated={load} />
      </div>
    </PlatformLayout>
  );
}
